import { Link } from "react-router-dom";
import {
  Truck,
  ShieldCheck,
  Package,
  Sparkles,
  Calendar,
  Boxes,
  Layers,
  GlassWater,
  UtensilsCrossed,
  Scale,
  AlertTriangle,
  ArrowRight,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Livraison à domicile ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Livraison à domicile
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Votre livraison de cartons à domicile {city.aCity}
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Parce que votre temps est précieux et que transporter des
            volumes de fournitures encombrantes n&apos;est pas votre
            métier, notre service logistique gère tout de A à Z.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {[
            {
              title: "Livraison sur-mesure",
              body: "Nous acheminons l'intégralité de vos commandes directement sur le pas de votre porte ou dans le garage de votre propriété à Saint-Nom-la-Bretèche, ainsi qu'à Noisy-le-Roi, Bailly, Feucherolles, Chavenay et Villepreux.",
              Icon: Truck,
            },
            {
              title: "Ponctualité rigoureuse",
              body: "Aucun créneau horaire flou qui bloque votre journée. Nous fixons ensemble un rendez-vous précis et strictement respecté selon vos disponibilités pour vous permettre d'avancer efficacement.",
              Icon: Calendar,
            },
            {
              title: "Protection des fournitures",
              body: "Tous vos cartons vous sont remis entièrement à plat, sous film de protection étanche, propres et secs, prêts pour un montage immédiat.",
              Icon: ShieldCheck,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-white rounded-2xl border border-[#cfdbe6] p-5 md:p-6"
            >
              <div className="flex items-center gap-2.5">
                <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[15px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Pack "Art de la Table" ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="overline" style={{ color: "#237cb5" }}>
              Pack &laquo;&nbsp;Art de la Table&nbsp;&raquo;
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Protégez vos verres et vaisselles fines
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Les demeures de {city.name} abritent souvent de belles
              tables, des services en porcelaine ou de la verrerie en
              cristal délicate. Pour éviter le moindre bris ou fêlure
              lors du transport routier, la boîte classique ne suffit
              pas.
            </p>

            <div className="mt-6 space-y-4">
              {[
                {
                  title: "Le carton à croisillons pour verres",
                  body: "C'est le secret des professionnels. Ce carton spécifique intègre des alvéoles rigides en carton lourd qui isolent chaque verre individuellement. Plus aucun contact direct n'est possible, éliminant à 100% les risques de casse liés aux vibrations du camion.",
                  Icon: GlassWater,
                },
                {
                  title: "Le carton à croisillons pour assiettes",
                  body: "Conçu sur le même principe avec des intercalaires horizontaux, il vous permet d'empiler vos assiettes à plat sans qu'elles ne s'entrechoquent. Un gain de temps fou à l'emballage puisqu'il n'est plus nécessaire d'envelopper chaque assiette dans des mètres de papier.",
                  Icon: UtensilsCrossed,
                },
              ].map(({ title, body, Icon }) => (
                <div
                  key={title}
                  className="flex items-start gap-4 bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
                >
                  <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                    <Icon className="h-5 w-5" strokeWidth={2.4} />
                  </span>
                  <div>
                    <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                      {title}
                    </h3>
                    <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                      {body}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <Link
              to="/cartons-demenagement?categorie=croisillons"
              className="mt-7 inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
              data-testid="city-saint-nom-la-breteche-cta-croisillons"
            >
              <GlassWater className="h-4 w-4" strokeWidth={2.6} />
              Voir les cartons à croisillons
              <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
            </Link>
          </div>

          <div className="lg:col-span-5 bg-[#EEF3F7] border border-[#cfdbe6] rounded-3xl p-6">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center h-12 w-12 rounded-full bg-[#237cb5] text-white">
                <UtensilsCrossed className="h-6 w-6" strokeWidth={2.4} />
              </span>
              <div className="font-display font-extrabold text-[20px] text-[#0f1d2b] leading-tight">
                Zéro casse à l&apos;arrivée
              </div>
            </div>
            <p className="mt-4 text-[14px] text-[#3a4856] leading-relaxed">
              Vos verres et vos assiettes voyagent isolés un à un.
              Aucun contact, aucun choc transmis : la porcelaine et le
              cristal arrivent intacts.
            </p>
            <div className="mt-5 space-y-2.5 text-[13px] text-[#3a4856]">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Alvéoles rigides en carton lourd
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Intercalaires horizontaux assiettes
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Gain de temps à l&apos;emballage
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Idéal verres &amp; verrerie en cristal
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Optimiser le rangement ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Méthode pro
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Comment optimiser le rangement de ses cartons&nbsp;?
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Remplir un carton de déménagement ne se fait pas au hasard.
            Pour maximiser l&apos;espace de chargement et garantir la
            sécurité de vos biens, appliquez ces trois règles d&apos;or
            des professionnels.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {[
            {
              n: "Règle 1",
              title: "Lourd en bas, léger en haut",
              body: "Placez toujours les objets les plus denses et lourds au fond du carton (comme les gros livres ou les bas de plats) pour stabiliser la boîte. Disposez les éléments plus légers par-dessus pour combler l'espace sans surcharger le poids total.",
              Icon: Scale,
            },
            {
              n: "Règle 2",
              title: "Éliminez le vide à l'intérieur",
              body: "Un carton qui contient du vide s'écrase inévitablement lorsqu'il est empilé sous d'autres boîtes dans le camion. Utilisez du papier kraft froissé, du film à bulles ou même votre linge de maison (torchons, serviettes) pour caler les objets et remplir chaque recoin.",
              Icon: Layers,
            },
            {
              n: "Règle 3",
              title: "Ne dépassez pas la limite de poids",
              body: "Même si nos cartons professionnels sont ultra-résistants, veillez à ne pas dépasser 20 à 25 kg par boîte. Si un carton est trop lourd, il devient dangereux à manipuler pour votre dos et augmente le risque de glissade lors du portage.",
              Icon: AlertTriangle,
            },
          ].map(({ n, title, body, Icon }) => (
            <div
              key={n}
              className="flex items-start gap-4 bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
            >
              <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <Icon className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <div>
                <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#237cb5]">
                  {n}
                </div>
                <h3 className="mt-1 font-display font-extrabold text-[16px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
                <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/emballage-protection"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-saint-nom-la-breteche-cta-protection"
          >
            <Layers className="h-4 w-4" strokeWidth={2.6} />
            Voir nos solutions de calage
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/cartons-demenagement"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-saint-nom-la-breteche-cta-shop"
          >
            <Boxes className="h-4 w-4" strokeWidth={2.6} />
            Voir tous les cartons
          </Link>
        </div>
      </div>
    </section>

    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-t border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-saint-nom-la-breteche-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
  </>
);

export default { Sections };
